import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, IconButton, Step, StepLabel,
  Stepper, Tooltip, Typography,
} from '@mui/material';
import { ArrowBack, ArrowForward, EditOutlined, Redo, Send, Undo } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import type { PublicIntakeDraft, PublicIntakeView } from '../../../types/intake';
import {
  getPublicIntake, savePublicIntakeDraft, submitPublicIntake, reopenPublicIntake,
  revisePublicIntake,
} from '../../../services/publicIntakeService';
import { getClients } from '../../../services/clientService';
import { getProjects } from '../../../services/projectService';
import { getLocationsByProject } from '../../../services/locationService';
import { RequestSummary } from './components/RequestSummary';
import { ExcelRoundTrip } from './components/ExcelRoundTrip';
import {
  validateProjectInfo, validateSamples, validateChecklist, validateAll,
  type ProjectInfoField, type SampleField,
} from './intakeFormState';
import { ProjectInfoStep, type IntakeDirectory } from './steps/ProjectInfoStep';
import { SamplesGridStep } from './steps/SamplesGridStep';
import { ReviewStep } from './steps/ReviewStep';
import { SavedIndicator, type SaveState } from './components/SavedIndicator';

const STEPS = ['Project information', 'Samples & tests', 'Review & confirm'];
const AUTOSAVE_MS = 1500;

/**
 * The public (tokenized-link) client intake form — the in-app replica of the
 * KeyLAB request workbook. Enter the project, pick the tests per sample, then
 * REVIEW everything and confirm the workbook's checklist as the final step.
 * Autosaves as a draft; submit locks it for the lab to review.
 */
export const PublicIntakePage = () => {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<PublicIntakeView | null>(null);
  const [draft, setDraft] = useState<PublicIntakeDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [stepErrors, setStepErrors] = useState<string[]>([]);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [errorFields, setErrorFields] = useState<ProjectInfoField[]>([]);
  const [errorRows, setErrorRows] = useState<Record<number, SampleField[]>>({});
  const [flashNonce, setFlashNonce] = useState(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirty = useRef(false);
  // Undo/redo — every edit snapshots the previous draft (bounded stack).
  const undoStack = useRef<PublicIntakeDraft[]>([]);
  const redoStack = useRef<PublicIntakeDraft[]>([]);
  const [historyVersion, setHistoryVersion] = useState(0);
  const HISTORY_LIMIT = 200;

  // Internal mode (2026-08-04): this is a BGC-internal process for now. A
  // signed-in user gets pickers over EXISTING clients/projects/locations —
  // attach, don't retype. Anonymous token links (future client use) silently
  // stay free-text: the directory calls simply fail and stay null.
  const { accounts } = useMsal();
  const isSignedIn = accounts.length > 0;
  const [directory, setDirectory] = useState<IntakeDirectory | null>(null);
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  useEffect(() => {
    if (!isSignedIn) return;
    let live = true;
    Promise.all([
      getClients(),
      getProjects({ page: 0, pageSize: 10000 }),
    ]).then(([clients, projects]) => {
      if (live) setDirectory({ clients, projects: projects.data });
    }).catch(() => { /* not authorized here → stay free-text */ });
    return () => { live = false; };
  }, [isSignedIn]);
  const draftProjectKey = draft?.projectKey ?? null;
  useEffect(() => {
    if (!isSignedIn || !draftProjectKey) { setLocationOptions([]); return; }
    let live = true;
    getLocationsByProject(draftProjectKey)
      .then((locs) => { if (live) setLocationOptions(locs.map((l) => l.LocationId).filter(Boolean)); })
      .catch(() => { if (live) setLocationOptions([]); });
    return () => { live = false; };
  }, [isSignedIn, draftProjectKey]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const v = await getPublicIntake(token);
      setView(v);
      setDraft((prev) => prev ?? {
        ...v.draft,
        samples: v.draft.samples.length > 0 ? v.draft.samples : [{
          locationId: null, topDepth: null, baseDepth: null, sampleRef: null, sampleType: null, tests: [],
        }],
      });
    } catch (err) {
      setLoadError((err as Error).message || 'This link is not valid.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const editable = Boolean(
    view && !view.expired && (view.status === 'Draft' || view.status === 'Returned') && !submitted,
  );

  // Debounced autosave — only while editable and after a real edit.
  const scheduleSave = useCallback((next: PublicIntakeDraft) => {
    dirty.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!token || !dirty.current) return;
      dirty.current = false;
      setSaveState('saving');
      try {
        const { savedAt: at } = await savePublicIntakeDraft(token, next);
        setSavedAt(at);
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, AUTOSAVE_MS);
  }, [token]);

  const updateDraft = useCallback((patch: Partial<PublicIntakeDraft>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      undoStack.current.push(prev);
      if (undoStack.current.length > HISTORY_LIMIT) undoStack.current.shift();
      redoStack.current = [];
      const next = { ...prev, ...patch };
      scheduleSave(next);
      return next;
    });
    setHistoryVersion((v) => v + 1);
    setStepErrors([]);
    setErrorFields([]);
    setErrorRows({});
  }, [scheduleSave]);

  const undo = useCallback(() => {
    setDraft((prev) => {
      const snapshot = undoStack.current.pop();
      if (!prev || !snapshot) return prev;
      redoStack.current.push(prev);
      scheduleSave(snapshot);
      return snapshot;
    });
    setHistoryVersion((v) => v + 1);
  }, [scheduleSave]);

  const redo = useCallback(() => {
    setDraft((prev) => {
      const snapshot = redoStack.current.pop();
      if (!prev || !snapshot) return prev;
      undoStack.current.push(prev);
      scheduleSave(snapshot);
      return snapshot;
    });
    setHistoryVersion((v) => v + 1);
  }, [scheduleSave]);

  // Ctrl+Z / Ctrl+Y (or Ctrl+Shift+Z) — outside text inputs, where the
  // browser's own in-field text undo should keep working.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault(); undo();
      } else if (
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z')
      ) {
        e.preventDefault(); redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const goNext = () => {
    if (!draft || !view) return;
    const check = step === 0
      ? validateProjectInfo(draft)
      : validateSamples(draft, view.formDef);
    if (check.errors.length > 0) {
      setStepErrors(check.errors);
      setErrorFields(check.fields ?? []);
      setErrorRows(check.rows ?? {});
      setFlashNonce((n) => n + 1);
      return;
    }
    setStepErrors([]);
    setErrorFields([]);
    setErrorRows({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const checklistComplete = useMemo(() => (
    draft && view ? validateChecklist(draft, view.formDef).errors.length === 0 : false
  ), [draft, view]);

  const reviewWarnings = useMemo(() => (
    draft && view ? validateSamples(draft, view.formDef).warnings : []
  ), [draft, view]);

  const handleSubmit = async () => {
    if (!token || !draft || !view) return;
    const all = validateAll(draft, view.formDef);
    if (all.errors.length > 0) {
      setStepErrors(all.errors);
      // Jump back to the step with the problem and flash its fields.
      const info = validateProjectInfo(draft);
      const samplesCheck = validateSamples(draft, view.formDef);
      setErrorFields(info.fields ?? []);
      setErrorRows(samplesCheck.rows ?? {});
      setFlashNonce((n) => n + 1);
      if ((info.fields ?? []).length > 0) setStep(0);
      else if (Object.keys(samplesCheck.rows ?? {}).length > 0 || samplesCheck.errors.length > 0) setStep(1);
      return;
    }
    setStepErrors([]);
    setSubmitting(true);
    setServerError(null);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await submitPublicIntake(token, draft);
      setSubmitted(true);
    } catch (err) {
      const e = err as Error & { status?: number };
      setServerError(e.message || 'Submit failed — please try again.');
      // Checklist/form drift: refresh the form definition so re-confirming works.
      if (e.status === 400) {
        try {
          const fresh = await getPublicIntake(token);
          setView((prev) => (prev ? { ...prev, formDef: fresh.formDef } : fresh));
        } catch { /* keep the stale def; the error text still shows */ }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render branches ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Center><CircularProgress sx={{ color: '#3E4A3E' }} /></Center>
    );
  }
  if (loadError || !view || !draft) {
    return (
      <Center>
        <Alert severity="error" sx={{ maxWidth: 520 }}>
          {loadError || 'This link is not valid.'} If you believe this is a mistake, contact the lab.
        </Alert>
      </Center>
    );
  }

  const isSubmittedView = submitted || view.status === 'Submitted';
  const isConvertedView = view.status === 'ConvertedQuote' || view.status === 'ConvertedWorkOrder';
  const canReopen = isSubmittedView && !view.expired;
  // Import-first (2026-08-04): field crews almost always have an Excel — an
  // empty grid leads with the import card instead of hand-entry.
  const gridEmpty = draft.samples.every(
    (s) => !s.locationId && s.topDepth == null && !s.sampleRef && s.tests.length === 0,
  );

  // Lab already processed it → clone into a fresh draft on a new link.
  const handleRevise = async () => {
    if (!token) return;
    setReopening(true);
    try {
      const { token: newToken } = await revisePublicIntake(token);
      window.location.href = `/intake/${newToken}`;
    } catch (err) {
      setServerError((err as Error).message || 'Could not start a revised request.');
      setReopening(false);
    }
  };

  /** Confirmed import (ours or legacy KeyLAB) — applied as a normal,
   *  UNDOABLE edit through updateDraft (which also autosaves). */
  const handleImportApply = (result: { fields: Partial<Record<string, unknown>>; samples: PublicIntakeDraft['samples'] }) => {
    const fieldPatch: Partial<PublicIntakeDraft> = {};
    for (const key of ['projectId', 'projectName', 'projectLocation', 'clientName', 'contactName', 'contactEmail', 'dueDate', 'clientNotes'] as const) {
      if (result.fields[key] != null) (fieldPatch as Record<string, unknown>)[key] = result.fields[key];
    }
    // An import that names a DIFFERENT client/project drops the attachment —
    // otherwise samples would silently land under the previously-picked one.
    const differs = (imported: unknown, current: string | null) =>
      imported != null && String(imported).trim().toLowerCase() !== (current ?? '').trim().toLowerCase();
    if (draft?.clientKey && differs(result.fields.clientName, draft.clientName)) fieldPatch.clientKey = null;
    if (draft?.projectKey && (differs(result.fields.projectName, draft.projectName) || differs(result.fields.projectId, draft.projectId))) {
      fieldPatch.projectKey = null;
    }
    updateDraft({ ...fieldPatch, samples: result.samples });
    setStep(1); // land on the grid so the imported samples are visible
  };

  const handleReopen = async () => {
    if (!token) return;
    setReopening(true);
    setLoadError(null);
    try {
      await reopenPublicIntake(token);
      const fresh = await getPublicIntake(token);
      setView(fresh);
      setDraft(fresh.draft.samples.length > 0 ? fresh.draft : {
        ...fresh.draft,
        samples: [{ locationId: null, topDepth: null, baseDepth: null, sampleRef: null, sampleType: null, tests: [] }],
      });
      undoStack.current = [];
      redoStack.current = [];
      setSubmitted(false);
      setServerError(null);
      setStep(0);
    } catch (err) {
      setServerError((err as Error).message || 'Could not reopen the request.');
    } finally {
      setReopening(false);
    }
  };

  const statusNotice = (() => {
    if (isSubmittedView) {
      return { severity: 'success' as const, text: 'Thank you! Your test request has been submitted. The lab will review it and be in touch.' };
    }
    if (isConvertedView) {
      return { severity: 'info' as const, text: 'This request has been received and is being processed by the lab. Contact the lab directly for any changes.' };
    }
    if (view.status === 'Cancelled') {
      return { severity: 'warning' as const, text: 'This intake link has been cancelled by the lab. Contact the lab if you still need testing.' };
    }
    if (view.expired) {
      return { severity: 'warning' as const, text: 'This intake link has expired. Contact the lab for a new link.' };
    }
    return null;
  })();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, px: 2, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Card sx={{ maxWidth: 1560, width: '100%', borderRadius: '12px', alignSelf: 'flex-start' }} elevation={3}>
        <Box sx={{ bgcolor: '#3E4A3E', color: '#fff', py: 2.5, px: 4, borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>BGC Engineering</Typography>
            <Typography variant="subtitle2" sx={{ opacity: 0.85, mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              Geotechnical Laboratory — Test Request
              {isSignedIn && (
                <Chip label="BGC internal" size="small"
                  sx={{ height: 18, fontSize: '0.62rem', color: '#EEF2E6', bgcolor: 'rgba(255,255,255,0.16)' }} />
              )}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {editable && (
              <Box sx={{ display: 'flex', gap: 0.25 }}>
                <Tooltip title="Undo (Ctrl+Z)">
                  <span>
                    <IconButton size="small" onClick={undo}
                      disabled={undoStack.current.length === 0}
                      sx={{ color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
                      data-history-version={historyVersion}>
                      <Undo sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Redo (Ctrl+Y)">
                  <span>
                    <IconButton size="small" onClick={redo}
                      disabled={redoStack.current.length === 0}
                      sx={{ color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
                      <Redo sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
            <SavedIndicator state={saveState} savedAt={savedAt} />
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 4 }, pt: { xs: 2, sm: 3 } }}>
          {statusNotice ? (
            <Box>
              <Alert severity={statusNotice.severity} sx={{ mb: (isSubmittedView || isConvertedView) ? 3 : 0 }}>
                {statusNotice.text}
              </Alert>
              {(isSubmittedView || isConvertedView) && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Your submitted request
                  </Typography>
                  <RequestSummary def={view.formDef} draft={draft} />
                  {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}
                  {canReopen && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button variant="outlined" startIcon={<EditOutlined />} onClick={handleReopen}
                        disabled={reopening} sx={{ textTransform: 'none' }}>
                        {reopening ? 'Reopening…' : 'Edit request'}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        You can edit and resubmit any time until the lab starts processing it.
                      </Typography>
                    </Box>
                  )}
                  {isConvertedView && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button variant="outlined" startIcon={<EditOutlined />} onClick={handleRevise}
                        disabled={reopening} sx={{ textTransform: 'none' }}>
                        {reopening ? 'Starting…' : 'Start a revised request'}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        The lab is already working from this version — a revision opens a fresh
                        copy of everything above on a new link for you to change and submit.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            <>
              {view.status === 'Returned' && view.returnNote && (
                <Alert severity="warning" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
                  <strong>The lab returned this request for changes:</strong>{'\n'}{view.returnNote}
                </Alert>
              )}

              <Stepper activeStep={step} sx={{ mb: 3, maxWidth: 900 }}>
                {STEPS.map((label) => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>

              {stepErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStepErrors([])}>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {stepErrors.map((e) => <li key={e}>{e}</li>)}
                  </Box>
                </Alert>
              )}

              {step === 0 && (
                <ProjectInfoStep
                  draft={draft} onChange={updateDraft}
                  instructionsBlurb={view.formDef.instructionsBlurb}
                  errorFields={errorFields} flashNonce={flashNonce}
                  directory={directory}
                />
              )}
              {step === 1 && (
                <>
                  {gridEmpty && editable && token && (
                    <ExcelRoundTrip token={token} def={view.formDef} onApply={handleImportApply} variant="hero" />
                  )}
                  <SamplesGridStep
                    def={view.formDef} draft={draft}
                    onChange={(samples) => updateDraft({ samples })}
                    errorRows={errorRows} flashNonce={flashNonce}
                    locationOptions={locationOptions}
                  />
                </>
              )}
              {step === 2 && (
                <ReviewStep
                  def={view.formDef} draft={draft}
                  onChecklistChange={(checklist) => updateDraft({ checklist })}
                  warnings={reviewWarnings}
                  serverError={serverError}
                />
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center' }}>
                {step > 0 && (
                  <Button startIcon={<ArrowBack />} onClick={() => { setStepErrors([]); setStep(step - 1); }}
                    sx={{ textTransform: 'none' }}>
                    Back
                  </Button>
                )}
                {editable && step < 2 && token && !(step === 1 && gridEmpty) && (
                  <ExcelRoundTrip token={token} def={view.formDef} onApply={handleImportApply} />
                )}
                {step < STEPS.length - 1 ? (
                  <Button variant="contained" endIcon={<ArrowForward />} onClick={goNext}
                    disabled={!editable} sx={{ textTransform: 'none', ml: 'auto' }}>
                    Continue
                  </Button>
                ) : (
                  <Button variant="contained" endIcon={<Send />} onClick={handleSubmit}
                    disabled={!editable || !checklistComplete || submitting}
                    sx={{ textTransform: 'none', ml: 'auto' }}>
                    {submitting ? 'Submitting…' : 'Submit request'}
                  </Button>
                )}
              </Box>
              {step === STEPS.length - 1 && !checklistComplete && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                  Confirm every checklist item above to enable submit.
                </Typography>
              )}
            </>
          )}
        </Box>
      </Card>
    </Box>
  );
};

const Center = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
    {children}
  </Box>
);

export default PublicIntakePage;
